import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '../../store';
import { Menu, X, ChevronDown, LogOut } from 'lucide-react';
import { Button } from '../ui/Components';
import { Dialog } from '../ui/Dialog';
import { tenants } from '../../services/mockData';
import { cn } from '../../lib/utils';
import logoImg from '/src/assets/logos/logo.webp';
import { authService } from '../../services/api/auth';
import { useNotification } from '../../hooks/useNotification';
import { NAV_ITEMS } from '../../constants/navigation';
// ...existing code...

export const Navbar = () => {
  const { isMenuOpen, toggleMenu, closeMenu, isAuthenticated, user, logout } = useStore();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isMobileProfileOpen, setIsMobileProfileOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [brandImgError, setBrandImgError] = useState(false);
  const [avatarImgError, setAvatarImgError] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const isTransparent = location.pathname === '/' && !isMenuOpen;

  // Reset avatar image error when user changes
  React.useEffect(() => {
    setAvatarImgError(false);
  }, [user?.avatar_url]);

  // Logic to dynamically determine the brand subtitle based on the current path
  const getBrandSubtitle = () => {
    if (location.pathname.includes('/unit/smp')) return 'Sekolah Menengah Pertama';
    if (location.pathname.includes('/unit/smk')) return 'Sekolah Menengah Kejuruan';
    if (location.pathname.includes('/unit/ponpes')) return 'Pondok Pesantren';
    return 'Yayasan Pendidikan Islam';
  };

  const brandSubtitle = getBrandSubtitle();

  const handleLogoutRequest = () => {
    if (isMenuOpen) closeMenu();
    if (isMobileProfileOpen) setIsMobileProfileOpen(false);
    setShowLogoutConfirm(true);
  };

  const confirmLogout = async () => {
    try {
      // Call authService logout to clear Supabase session
      await authService.logout();
      // Call store logout to clear Zustand state and localStorage
      logout();
      setShowLogoutConfirm(false);
      showNotification({ type: 'success', message: 'Anda telah keluar.', duration: 3000 });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if authService.logout fails, still clear local state
      logout();
      setShowLogoutConfirm(false);
      showNotification({ type: 'success', message: 'Anda telah keluar.', duration: 3000 });
      navigate('/login');
    }
  };

  return (
    <>
      <nav
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300 print:hidden',
          isTransparent
            ? 'bg-emerald-900/90 backdrop-blur-sm text-white py-4'
            : 'bg-white text-slate-800 shadow-md py-3',
        )}
      >
        <div className="container mx-auto px-4 md:px-6 flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" onClick={closeMenu}>
            <img
              src={logoImg}
              alt="Logo"
              className="h-12 w-12 object-contain"
              onError={() => setBrandImgError(true)}
              style={{ display: brandImgError ? 'none' : 'block' }}
              width={48}
              height={48}
            />
            {brandImgError && <span className="text-xs text-emerald-600">Logo</span>}
            <div className="flex flex-col">
              <span
                className={cn(
                  'text-[9px] sm:text-xxs md:text-xs font-medium uppercase tracking-wider mb-0',
                  isTransparent ? 'text-emerald-200' : 'text-slate-500',
                )}
              >
                {brandSubtitle}
              </span>
              <span
                className={cn(
                  'text-base md:text-xl font-bold font-serif leading-none',
                  isTransparent ? 'text-white' : 'text-emerald-900',
                )}
              >
                Mathla'un Nawakartika
              </span>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-6 font-medium text-sm">
            {NAV_ITEMS.map((item) => {
              if (item.type === 'dropdown') {
                return (
                  <div key={item.label} className="relative group">
                    <button
                      className="flex items-center gap-1 hover:text-emerald-600 transition-colors py-2"
                      onMouseEnter={() => setDropdownOpen(true)}
                      onMouseLeave={() => setDropdownOpen(false)}
                    >
                      {item.label} <ChevronDown className="h-4 w-4" />
                    </button>
                    {isDropdownOpen && (
                      <div
                        className="absolute top-full left-0 w-56 bg-white rounded-md shadow-lg border border-slate-100 py-2 flex flex-col animate-in fade-in zoom-in-95 duration-200"
                        onMouseEnter={() => setDropdownOpen(true)}
                        onMouseLeave={() => setDropdownOpen(false)}
                      >
                        {tenants.map((t) => (
                          <Link
                            key={t.id}
                            to={`/unit/${t.slug}`}
                            className="px-4 py-2 hover:bg-emerald-50 text-slate-700 text-left"
                          >
                            {t.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={item.path}
                  to={item.path!}
                  className="hover:text-emerald-600 transition-colors"
                >
                  {item.label}
                </Link>
              );
            })}

            {isAuthenticated && user ? (
              <div className="relative group">
                <button className="flex items-center gap-2 focus:outline-none py-1 cursor-pointer">
                  {/* Profile Picture Logic:
                      1. Student: Shows formal photo if uploaded (via CompleteProfile updating avatar_url).
                      2. Admin: Shows Google photo if available (via auth metadata).
                      3. Fallback: Shows initials if no photo is available.
                  */}
                  <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-emerald-100 bg-emerald-50 flex items-center justify-center text-emerald-700 font-bold shadow-sm transition-transform group-hover:scale-105 cursor-pointer">
                    {user.avatar_url && !avatarImgError ? (
                      <img
                        src={user.avatar_url}
                        alt={user.name}
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                        onError={() => setAvatarImgError(true)}
                      />
                    ) : (
                      <span>
                        {user.name
                          ? user.name
                              .split(' ')
                              .map((n: string) => n[0])
                              .join('')
                              .toUpperCase()
                              .substring(0, 2)
                          : 'U'}
                      </span>
                    )}
                  </div>
                </button>
                {/* Dropdown */}
                <div className="absolute right-0 top-full pt-2 w-56 hidden group-hover:block">
                  <div className="bg-white rounded-lg shadow-xl border border-slate-100 py-2 animate-in fade-in zoom-in-95 duration-200">
                    <div className="px-4 py-3 border-b border-slate-50 bg-slate-50/50">
                      <p className="text-sm font-bold text-slate-800 truncate">{user.name}</p>
                      <p className="text-xs text-slate-500 truncate">{user.email}</p>
                    </div>
                    {user.role === 'student' ? (
                      <>
                        <Link
                          to="/dashboard"
                          className="block px-4 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                        >
                          Dashboard
                        </Link>
                        <Link
                          to="/profile"
                          className="block px-4 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                        >
                          Profil Saya
                        </Link>
                        <Link
                          to="/spmb/register"
                          className="block px-4 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                        >
                          Daftar SPMB
                        </Link>
                        <Link
                          to="/scholarship-register"
                          className="block px-4 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                        >
                          Daftar Beasiswa
                        </Link>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/admin"
                          className="block px-4 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                        >
                          Admin Dashboard
                        </Link>
                      </>
                    )}
                    <button
                      onClick={handleLogoutRequest}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 cursor-pointer"
                    >
                      <LogOut className="w-3 h-3" /> Logout
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <Link to="/login">
                <Button size="sm" variant={isTransparent ? 'secondary' : 'primary'}>
                  Login
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Toggle: for `student` and admin roles show avatar instead of hamburger */}
          {isAuthenticated &&
          user &&
          (user.role === 'student' ||
            user.role === 'admin' ||
            user.role === 'admin_smp' ||
            user.role === 'admin_smk' ||
            user.role === 'admin_ponpes') ? (
            <button
              className="md:hidden cursor-pointer"
              onClick={() => setIsMobileProfileOpen((v) => !v)}
              aria-label="Buka menu profil"
            >
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-emerald-100 bg-emerald-50 flex items-center justify-center text-emerald-700 font-bold shadow-sm">
                {user.avatar_url && !avatarImgError ? (
                  <img
                    src={user.avatar_url}
                    alt={user.name}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                    onError={() => setAvatarImgError(true)}
                  />
                ) : (
                  <span className="text-sm">
                    {user.name
                      ? user.name
                          .split(' ')
                          .map((n: string) => n[0])
                          .join('')
                          .toUpperCase()
                          .substring(0, 2)
                      : 'U'}
                  </span>
                )}
              </div>
            </button>
          ) : (
            <button className="md:hidden" onClick={toggleMenu} aria-label="Toggle menu">
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className={cn('h-6 w-6', isTransparent ? 'text-white' : 'text-slate-800')} />
              )}
            </button>
          )}
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b border-slate-200 shadow-xl p-4 flex flex-col gap-4 text-slate-800 animate-in slide-in-from-top-5">
            <Link to="/" onClick={closeMenu} className="py-2 border-b border-slate-100">
              Beranda
            </Link>
            <Link to="/about" onClick={closeMenu} className="py-2 border-b border-slate-100">
              Tentang Yayasan
            </Link>
            <div className="flex flex-col gap-2 py-2 border-b border-slate-100">
              <span className="text-xs font-bold text-slate-400 uppercase">Unit Pendidikan</span>
              {tenants.map((t) => (
                <Link
                  key={t.id}
                  to={`/unit/${t.slug}`}
                  onClick={closeMenu}
                  className="pl-4 py-1 text-emerald-700 font-medium"
                >
                  {t.name}
                </Link>
              ))}
            </div>
            <Link to="/scholarships" onClick={closeMenu} className="py-2 border-b border-slate-100">
              Beasiswa
            </Link>
            <Link to="/news" onClick={closeMenu} className="py-2 border-b border-slate-100">
              Berita
            </Link>
            <Link
              to="/spmb"
              onClick={closeMenu}
              className="py-2 border-b border-slate-100 font-bold text-emerald-700"
            >
              SPMB Online
            </Link>
            <Link to="/faq" onClick={closeMenu} className="py-2 border-b border-slate-100">
              FAQ / Bantuan
            </Link>
            <Link to="/career" onClick={closeMenu} className="py-2 border-b border-slate-100">
              Karir & Lowongan
            </Link>

            {isAuthenticated ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={closeMenu}
                  className="py-2 border-b border-slate-100 font-bold"
                >
                  Dashboard Siswa
                </Link>
                <Link
                  to="/profile"
                  onClick={closeMenu}
                  className="py-2 border-b border-slate-100 font-bold"
                >
                  Profil Saya
                </Link>
                <Button
                  className="w-full cursor-pointer"
                  variant="outline"
                  onClick={handleLogoutRequest}
                >
                  Logout
                </Button>
              </>
            ) : (
              <Link to="/login" onClick={closeMenu}>
                <Button className="w-full">Login</Button>
              </Link>
            )}
          </div>
        )}

        {/* Mobile profile dropdown for authenticated users (students and admins) */}
        {isMobileProfileOpen && (
          <div className="md:hidden absolute top-full right-4 w-56 bg-white rounded-lg shadow-xl border border-slate-100 py-2 animate-in slide-in-from-top-5">
            <div className="bg-white rounded-lg shadow-none py-2">
              <div className="px-4 py-3 border-b border-slate-50 bg-slate-50/50">
                <p className="text-sm font-bold text-slate-800 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
              {user?.role === 'student' ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setIsMobileProfileOpen(false)}
                    className="block px-4 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/profile"
                    onClick={() => setIsMobileProfileOpen(false)}
                    className="block px-4 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                  >
                    Profil Saya
                  </Link>
                  <Link
                    to="/spmb/register"
                    onClick={() => setIsMobileProfileOpen(false)}
                    className="block px-4 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                  >
                    Daftar SPMB
                  </Link>
                  <Link
                    to="/scholarship-register"
                    onClick={() => setIsMobileProfileOpen(false)}
                    className="block px-4 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                  >
                    Daftar Beasiswa
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/admin"
                    onClick={() => setIsMobileProfileOpen(false)}
                    className="block px-4 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                  >
                    Admin Dashboard
                  </Link>
                </>
              )}
              <button
                onClick={handleLogoutRequest}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 cursor-pointer"
              >
                <LogOut className="w-3 h-3" /> Logout
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Logout Confirmation Dialog */}
      <Dialog
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        title="Konfirmasi Logout"
        className="max-w-sm"
      >
        <div className="p-6 pt-0">
          <p className="text-slate-600 mb-6">Apakah anda yakin ingin keluar dari aplikasi?</p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowLogoutConfirm(false)}>
              Batal
            </Button>
            <Button
              variant="primary"
              className="bg-red-600 hover:bg-red-700 text-white border-transparent"
              onClick={confirmLogout}
            >
              Ya, Keluar
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};
